import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {isEqual} from "lodash";

/** A line of the local draft: saved lines are keyed by their id, new ones by a local key. */
export interface DraftLine {
    key: string
    item: FolioItem
}

let localKeyCounter = 0;
const nextLocalKey = () => `new-${++localKeyCounter}`;

export const draftLinesFrom = (items: FolioItem[]): DraftLine[] => items
    .filter(item => !item.archived)
    .map(item => ({key: item.id ?? nextLocalKey(), item}));

export interface NewLineDefaults {
    folioId?: string
    /** The reservation's start and end: a new line starts at its time, with its duration. */
    start: Date
    end: Date
    currency: string
}

/** "Choose a service": no service, no price yet. */
export const newDraftLine = ({folioId, start, end, currency}: NewLineDefaults): DraftLine => ({
    key: nextLocalKey(),
    item: {folioId, timeSpanStart: start, timeSpanEnd: end, description: '', serviceInventoryCode: '', price: {currency}},
});

// Written by the backend: a copy must not carry them
const SERVER_OWNED_FIELDS = ['id', 'insertDate', 'updateDate', 'archived', 'live', 'totalAmount', 'sequenceId', 'fiasPostingLog'];

export const duplicateLine = (line: DraftLine): DraftLine => {
    const copy: Record<string, unknown> = {...line.item};
    SERVER_OWNED_FIELDS.forEach(field => delete copy[field]);
    return {key: nextLocalKey(), item: copy as FolioItem};
};

/** A new line nobody filled in: it isn't saved. */
export const isBlankNewLine = (item: FolioItem): boolean => (
    !item.id && !item.serviceId && !item.description?.trim() && item.price?.amountCents == null
);

// null and missing mean the same to the backend; dates compare by their time
const normalize = (value: unknown): unknown => {
    if (value instanceof Date) return value.getTime();
    if (Array.isArray(value)) return value.map(normalize);
    if (value && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
            if (entry != null) result[key] = normalize(entry);
        });
        return result;
    }
    return value;
};

const comparable = (value: unknown) => (value == null ? undefined : normalize(value));

/** The fields [after] changed against [before], with their new values (undefined: cleared). */
export const changedFields = (before: FolioItem, after: FolioItem): Partial<FolioItem> => {
    const previous = before as Record<string, unknown>;
    const next = after as Record<string, unknown>;
    const changes: Record<string, unknown> = {};
    Array.from(new Set(Object.keys(previous).concat(Object.keys(next)))).forEach(key => {
        if (!isEqual(comparable(previous[key]), comparable(next[key]))) changes[key] = next[key];
    });
    return changes as Partial<FolioItem>;
};

export const sameItem = (a: FolioItem, b: FolioItem): boolean => Object.keys(changedFields(a, b)).length === 0;

/**
 * The draft on top of newer saved lines: each line keeps the fields the draft changed and takes the
 * rest from its saved version (so e.g. a repriced reservation line keeps its new price), a line the
 * server no longer has goes, and a line added there joins at the end.
 */
export const rebaseDraft = (baseline: FolioItem[], lines: DraftLine[], server: FolioItem[]): DraftLine[] => {
    const before = new Map<string, FolioItem>();
    baseline.forEach(item => {
        if (item.id) before.set(item.id, item);
    });
    const now = new Map<string, FolioItem>();
    server.forEach(item => {
        if (item.id && !item.archived) now.set(item.id, item);
    });

    const inDraft = new Set<string>();
    const rebased = lines.reduce<DraftLine[]>((result, line) => {
        const id = line.item.id;
        if (!id) return [...result, line];
        inDraft.add(id);
        const saved = now.get(id);
        if (!saved) return result;
        return [...result, {key: line.key, item: {...saved, ...changedFields(before.get(id) ?? saved, line.item)}}];
    }, []);

    const added = server.filter(item => !!item.id && !item.archived && !before.has(item.id) && !inDraft.has(item.id));
    return [...rebased, ...draftLinesFrom(added)];
};

export interface DraftChanges {
    /** New lines, with their local keys, so the saved ids can be matched up. */
    create: DraftLine[]
    update: FolioItem[]
    /** Ids of saved lines the draft no longer has. */
    delete: string[]
}

/**
 * What saving the draft takes: new lines to create (blank ones are dropped), changed lines to
 * update and removed lines to delete. A line whose id the server no longer lists isn't updated.
 */
export const diffDraft = (server: FolioItem[], draft: DraftLine[]): DraftChanges => {
    const saved = new Map<string, FolioItem>();
    server.forEach(item => {
        if (item.id && !item.archived) saved.set(item.id, item);
    });

    const kept = new Set<string>();
    const create: DraftLine[] = [];
    const update: FolioItem[] = [];
    draft.forEach(line => {
        const id = line.item.id;
        if (!id) {
            if (!isBlankNewLine(line.item)) create.push(line);
            return;
        }
        kept.add(id);
        const before = saved.get(id);
        if (before && !sameItem(before, line.item)) update.push(line.item);
    });

    const remove = Array.from(saved.keys()).filter(id => !kept.has(id));
    return {create, update, delete: remove};
};

export const hasChanges = (changes: DraftChanges): boolean => (
    changes.create.length > 0 || changes.update.length > 0 || changes.delete.length > 0
);
